import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useOnboarding } from './ImportJobContext';
import { demoTeamNames, isDemoTeamOn } from './demoTeam';
import { IconClose } from './icons';
import { hapticTick, hapticSuccess } from '../../_utils/haptics';
import './DiaryTour.css';

/**
 * The walk round the diary, before anybody is asked to do anything.
 *
 * A new owner lands on a grid they have never seen and the old first task
 * said "book someone in". This shows them the room first: their own column,
 * the two temporary columns beside it, the services that arrived with setup,
 * and finally where to tap to make a booking. Four steps, about twenty
 * seconds, skippable at any point.
 *
 * Built for a finger: the card sits at the bottom on phones, the spotlight
 * ring is drawn with one box shadow so it costs nothing to move, and each
 * step gives a light haptic tick inside the app.
 */

const STEPS = [
  {
    key: 'yours',
    target: '.dv2-staff-column:not(.dv2-staff-column--demo) .dv2-staff-header',
    title: 'This column is yours',
    body: 'Your day runs top to bottom. Your name sits at the top, with the hours you work underneath.',
  },
  {
    key: 'team',
    target: '.dv2-staff-column--demo',
    title: (names) => (names.length ? `${names.join(' and ')} are examples` : 'Two example columns'),
    body: 'We added two columns with a few bookings, so you can see a working day. They vanish when you add your own team, and nothing in them is real.',
    needsDemoTeam: true,
    union: true, // both columns, not just the first
  },
  {
    key: 'services',
    target: '.dv2-staff-column:not(.dv2-staff-column--demo) .dv2-event-block',
    title: 'Your services are already in',
    body: 'Every booking is a client, a service and a time. Tap one to open it, check it out or move it.',
  },
  {
    key: 'tap',
    target: '.dv2-staff-column:not(.dv2-staff-column--demo) .dv2-column-body',
    title: 'Tap any empty space to book',
    body: 'That is how a booking starts. Pick the time you want in your column, then choose the client and the service.',
    cta: 'Let me try',
  },
];

const PAD = 6;

function useRect(selector, step, union) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!selector) { setRect(null); return undefined; }
    let frame = null;
    let lastScroll = 0;
    // Keep the target in view. The diary scrolls both ways and reflows as it
    // loads, so a single scrollIntoView at the start does not hold.
    const keepInView = (el) => {
      const r = el.getBoundingClientRect();
      const offX = r.left < 60 || r.right > window.innerWidth - 20;
      const offY = r.top > window.innerHeight - 180 || r.bottom < 120;
      if (!offX && !offY) return;
      const now = Date.now();
      if (now - lastScroll < 1600) return;
      lastScroll = now;
      // Instant, not smooth: the retry loop would cancel a smooth scroll
      // before it arrived, and the ring would never reach its target.
      try { el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'auto' }); } catch (e) { el.scrollIntoView(); }
    };
    const measure = () => {
      frame = null;
      const els = union ? [...document.querySelectorAll(selector)] : [document.querySelector(selector)].filter(Boolean);
      if (!els.length) { setRect(null); return; }
      // One ring around the lot when a step is about more than one thing.
      keepInView(els[0]);
      const boxes = els.map((e) => e.getBoundingClientRect());
      const r = {
        left: Math.min(...boxes.map((b) => b.left)),
        right: Math.max(...boxes.map((b) => b.right)),
        top: Math.min(...boxes.map((b) => b.top)),
        bottom: Math.max(...boxes.map((b) => b.bottom)),
        get width() { return this.right - this.left; },
      };
      // A column body runs the height of the day. Spotlight the part on
      // screen, not a ring that disappears off the bottom of the phone.
      const top = Math.max(r.top, 76);
      const bottom = Math.min(r.bottom, window.innerHeight - 120);
      setRect({ left: r.left, top, width: r.right - r.left, height: Math.max(bottom - top, 64) });
    };
    const schedule = () => { if (frame == null) frame = requestAnimationFrame(measure); };
    measure();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    // Measured straight from the timer, not through requestAnimationFrame:
    // frames are throttled when the window is in the background, and the ring
    // must still find its target when the diary reloads under it.
    const id = setInterval(measure, 500);
    return () => {
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      clearInterval(id);
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, [selector, step, union]);
  return rect;
}

export default function DiaryTour() {
  const ob = useOnboarding();
  const { enabled, onboarding, patchOnboarding } = ob || {};
  const checklist = (onboarding && onboarding.checklist) || {};
  const running = !!enabled && checklist.tour === 'pending' && onboarding && onboarding.status !== 'new';
  const [i, setI] = useState(0);

  // A step whose target is not on the page teaches nothing, so it is skipped
  // rather than showing a card pointing at thin air.
  const steps = STEPS.filter((s) => (!s.needsDemoTeam || isDemoTeamOn()) && (!s.target || document.querySelector(s.target)));
  const step = steps[Math.min(i, Math.max(steps.length - 1, 0))];
  const rect = useRect(running && step ? step.target : null, i, !!(step && step.union));

  const finish = useCallback((how) => {
    patchOnboarding({ checklist: { tour: 'done' } });
    if (how === 'end') hapticSuccess();
  }, [patchOnboarding]);

  useEffect(() => {
    if (!running) return undefined;
    document.body.classList.add('dtour-open');
    return () => document.body.classList.remove('dtour-open');
  }, [running]);

  useEffect(() => {
    if (!running) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') finish('skip');
      if (e.key === 'ArrowRight') setI((n) => Math.min(n + 1, steps.length - 1));
      if (e.key === 'ArrowLeft') setI((n) => Math.max(n - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [running, finish, steps.length]);

  if (!running || !step || !steps.length) return null;

  const last = i === steps.length - 1;
  const next = () => {
    hapticTick();
    if (last) finish('end'); else setI(i + 1);
  };
  const back = () => { hapticTick(); setI(Math.max(i - 1, 0)); };

  // The card sits under the ring when there is room, otherwise above it.
  const below = rect ? rect.top + rect.height + 14 : 140;
  const roomBelow = typeof window !== 'undefined' && below + 210 < window.innerHeight;
  const cardStyle = rect
    ? (roomBelow
      ? { top: below, left: Math.max(12, Math.min(rect.left, window.innerWidth - 360)) }
      : { top: Math.max(84, rect.top - 208), left: Math.max(12, Math.min(rect.left, window.innerWidth - 360)) })
    : { top: 120, left: 20 };

  const title = typeof step.title === 'function' ? step.title(demoTeamNames()) : step.title;

  return ReactDOM.createPortal(
    <div className="dtour" role="dialog" aria-modal="false" aria-label="A quick look round the diary">
      {rect && (
        <div
          className="dtour-ring"
          style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }}
        />
      )}
      {!rect && <div className="dtour-scrim" />}

      <section className="dtour-card" style={cardStyle}>
        <header className="dtour-head">
          <span className="dtour-step">{i + 1} of {steps.length}</span>
          <button type="button" className="dtour-x" onClick={() => finish('skip')} aria-label="Close the tour"><IconClose size={13} /></button>
        </header>
        <h3 className="dtour-title">{title}</h3>
        <p className="dtour-body">{step.body}</p>
        <div className="dtour-actions">
          {i > 0
            ? <button type="button" className="dtour-quiet" onClick={back}>Back</button>
            : <button type="button" className="dtour-quiet" onClick={() => finish('skip')}>Skip the tour</button>}
          <button type="button" className="dtour-next" onClick={next}>{step.cta || (last ? 'Done' : 'Next')}</button>
        </div>
        <ol className="dtour-dots" aria-hidden="true">
          {steps.map((s, n) => <li key={s.key} className={n === i ? 'is-on' : n < i ? 'is-done' : ''} />)}
        </ol>
      </section>
    </div>,
    document.body,
  );
}
